import { useCallback, useMemo, useState, type ReactNode } from "react";
import { AnimatePresence, motion } from "motion/react";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  Ban,
  CheckCircle2,
  KeyRound,
  Loader2,
  Lock,
  Save,
  Sparkles,
} from "lucide-react";

import { Button } from "@/modern/components/ui/button-shadcn";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/modern/components/ui/alert-dialog";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import { KeyValueModel } from "@/bryntum-lib/models/KeyValueModel";
import { DEMOTE_ASSET_CONFIRM_DESCRIPTION } from "@/react/components/entityViews/marketing/api/marketingAssets.constants";
import { getAvailableActions } from "@/react/features/promoteSurface/utils/actionMatrix";
import { toast } from "sonner";

import { useFileDetailModal } from "../../context/FileDetailModalContext";
import { PromotePanelProvider, usePromotePanel, PROMOTE_STEPS } from "./context/PromotePanelContext";
import { useOpenAIKeyModal } from "@/integrations";
import { AssetStatusChip } from "./components/AssetStatusBanner";
import { StepIndicator } from "./components/StepIndicator";
import { ClassifyStep } from "./components/ClassifyStep";
import { DescribeStep } from "./components/DescribeStep";
import { OrganizeStep } from "./components/OrganizeStep";
import { PromoteConfirmDialog } from "./components/PromoteConfirmDialog";
import { clearPromoteResumeStep } from "./promoteResumeStorage";

const STEP_COMPONENTS = [ClassifyStep, DescribeStep, OrganizeStep];

const LockedState = ({ onConfigureKey }: { onConfigureKey: () => void }) => (
  <div className="flex flex-1 items-center justify-center p-8">
    <div className="max-w-md w-full space-y-6 text-center">
      <div className="flex justify-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
          <Lock className="h-8 w-8 text-muted-foreground" />
        </div>
      </div>
      <div className="space-y-2">
        <h3 className="text-lg font-semibold text-foreground">AI Key Required</h3>
        <p className="text-sm text-muted-foreground">
          An OpenAI API key needs to be added to use AI insights. Add your API key to enable AI-powered analysis.
        </p>
      </div>
      <Button type="button" onClick={onConfigureKey} variant="outline" className="w-full">
        <KeyRound className="mr-2 h-4 w-4" />
        Configure Key
      </Button>
    </div>
  </div>
);

const PromoteSuccessView = ({
  onDone,
  isEditing,
  onViewAssets,
}: {
  onDone: () => void;
  isEditing: boolean;
  onViewAssets: () => void;
}) => (
  <motion.div
    role="dialog"
    aria-modal="true"
    aria-labelledby="promote-success-title"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
    className="flex min-h-0 flex-1 flex-col items-center justify-center px-5 py-8"
  >
    <div className="flex w-full max-w-sm flex-col items-center gap-5 text-center">
      <motion.div
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 320, damping: 22, delay: 0.05 }}
        className="relative"
      >
        <div className="absolute inset-0 scale-150 rounded-full bg-emerald-500/25 blur-xl" aria-hidden />
        <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-emerald-500/15 ring-2 ring-emerald-500/40 dark:bg-emerald-400/10 dark:ring-emerald-400/35">
          <CheckCircle2 className="h-10 w-10 text-emerald-600 dark:text-emerald-400" strokeWidth={1.75} />
        </div>
      </motion.div>

      <div className="space-y-1.5">
        <motion.p
          id="promote-success-title"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12, duration: 0.3 }}
          className="text-base font-semibold text-emerald-700 dark:text-emerald-400"
        >
          {isEditing ? "Changes saved successfully!" : "File promoted successfully!"}
        </motion.p>
        <motion.p
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.3 }}
          className="text-sm leading-relaxed text-muted-foreground"
        >
          {isEditing
            ? "Your promotion details have been updated."
            : "This file is promoted and ready for marketing."}
        </motion.p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.26, duration: 0.35 }}
        className="flex w-full flex-col gap-4"
      >
        <Button type="button" onClick={onDone} className="w-full gap-2">
          Done
        </Button>
        <button
          type="button"
          onClick={onViewAssets}
          className="inline-flex items-center justify-center gap-1.5 text-sm font-medium text-foreground transition-colors hover:text-primary"
        >
          View Promoted Assets
          <ArrowUpRight className="h-3.5 w-3.5" />
        </button>
      </motion.div>
    </div>
  </motion.div>
);

const PromotePanelInner = () => {
  const { handleClosePromotePanel, entityRecord, setOpen, file } = useFileDetailModal();
  const { userPreferences, editorHandler } = useSwivuBryntumContext();
  const openAiKeyModal = useOpenAIKeyModal();
  const [confirmDemote, setConfirmDemote] = useState(false);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const {
    currentStep,
    goNext,
    goBack,
    goToStep,
    canProceed,
    loading,
    submitting,
    promoted,
    onSubmit,
    isEditing,
    assetVerificationStatus,
    assetActionedByName,
    demoting,
    onDemote,
    form,
  } = usePromotePanel();

  useSwivuStore<KeyValueModel>({
    storeId: STORE_KEYS.boardPreferencesStore,
    subscriberFn: ({ record }) => {
      return record.key === PERSISTENCE_STATE_KEYS.hasOpenAiApiKey;
    },
  });

  const hasOpenAIKey = useMemo(() => {
    if (!userPreferences) return false;
    return userPreferences.getSetting(PERSISTENCE_STATE_KEYS.hasOpenAiApiKey, "false", true) === "true";
  }, [userPreferences]);

  const isLastStep = currentStep === PROMOTE_STEPS.length - 1;

  const handleDone = useCallback(() => {
    if (file?.id != null) {
      clearPromoteResumeStep(file.id);
    }
    handleClosePromotePanel();
  }, [handleClosePromotePanel, file?.id]);

  const handleViewAssets = useCallback(() => {
    setOpen(false);
    editorHandler.openEditor(
      EDITOR_KEYS.marketingAssetEditor,
      // @ts-expect-error - null recordIdObj when using by: "recordType"
      null,
      { entityRecord },
      { by: "recordType", useExisting: true },
    );
  }, [setOpen, editorHandler, entityRecord]);

  const handleConfigureKey = useCallback(() => {
    openAiKeyModal?.open();
  }, [openAiKeyModal]);

  const handleRequestSubmit = useCallback(async () => {
    if (!canProceed) return;
    const valid = await form.trigger();
    if (!valid) {
      const values = form.getValues();
      if (values.isRestricted && !values.restrictionReason) {
        goToStep(0);
        toast.error("Select a restriction reason before continuing.");
      } else {
        toast.error("Fix the highlighted fields before continuing.");
      }
      return;
    }
    setConfirmSubmit(true);
  }, [canProceed, form, goToStep]);

  const handleConfirmSubmit = useCallback(() => {
    onSubmit();
  }, [onSubmit]);

  const statusChip =
    assetVerificationStatus != null ? (
      <AssetStatusChip status={assetVerificationStatus} actionedByName={assetActionedByName} />
    ) : null;

  const canDemote = isEditing && getAvailableActions(assetVerificationStatus).reject;

  if (loading) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <PanelHeader onClose={handleClosePromotePanel} statusChip={statusChip} />
        <div className="flex flex-1 items-center justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center gap-4 text-muted-foreground"
          >
            <div className="relative">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
              >
                <Loader2 className="h-8 w-8" />
              </motion.div>
              <motion.div
                className="absolute inset-0 rounded-full"
                animate={{ opacity: [0.2, 0.5, 0.2] }}
                transition={{ duration: 2, repeat: Infinity }}
                style={{
                  background: "radial-gradient(circle, hsl(var(--primary) / 0.2) 0%, transparent 70%)",
                }}
              />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium">Analyzing image...</p>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  if (promoted) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <PromoteSuccessView onDone={handleDone} isEditing={isEditing} onViewAssets={handleViewAssets} />
      </div>
    );
  }

  if (!hasOpenAIKey) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <PanelHeader onClose={handleClosePromotePanel} isEditing={isEditing} statusChip={statusChip} />
        <LockedState onConfigureKey={handleConfigureKey} />
      </div>
    );
  }

  const StepComponent = STEP_COMPONENTS[currentStep];

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PanelHeader onClose={handleClosePromotePanel} isEditing={isEditing} statusChip={statusChip} />
      <div className="shrink-0 pt-1 pb-3">
        <StepIndicator />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pt-1 pb-3">
        <StepComponent />
      </div>

      <div className="shrink-0 space-y-2 border-t border-border/40 pt-3">
        <div className="flex items-center gap-2">
          {currentStep > 0 && (
            <motion.div
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              className="shrink-0"
            >
              <Button type="button" variant="outline" onClick={goBack} className="px-5">
                Back
              </Button>
            </motion.div>
          )}

          <AnimatePresence mode="wait">
            {isLastStep ? (
              <PromoteButton
                key="promote"
                submitting={submitting}
                onSubmit={handleRequestSubmit}
                isEditing={isEditing}
                disabled={!canProceed || submitting}
              />
            ) : (
              <motion.div
                key="continue"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex-1"
              >
                <Button
                  type="button"
                  onClick={goNext}
                  disabled={!canProceed}
                  className="w-full gap-2"
                >
                  Continue
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {canDemote && (
          <>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setConfirmDemote(true)}
              disabled={demoting || submitting}
              className="w-full gap-2 text-destructive hover:bg-destructive/10 hover:text-destructive"
            >
              {demoting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Ban className="h-3.5 w-3.5" />
              )}
              {demoting ? "Demoting..." : "Demote Asset"}
            </Button>

            <AlertDialog open={confirmDemote} onOpenChange={setConfirmDemote}>
              <AlertDialogContent
                overlayClassName="z-[calc(var(--swivu-bryntum-z-index)+20)]"
                className="z-[calc(var(--swivu-bryntum-z-index)+20)]"
              >
                <AlertDialogHeader>
                  <AlertDialogTitle>Demote Asset</AlertDialogTitle>
                  <AlertDialogDescription>
                    {DEMOTE_ASSET_CONFIRM_DESCRIPTION}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={onDemote}
                  >
                    Demote
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        )}
      </div>

      <PromoteConfirmDialog
        open={confirmSubmit}
        onOpenChange={setConfirmSubmit}
        isEditing={isEditing}
        submitting={submitting}
        onConfirm={handleConfirmSubmit}
      />
    </div>
  );
};

const PromoteButton = ({
  submitting,
  onSubmit,
  isEditing,
  disabled,
}: {
  submitting: boolean;
  onSubmit: () => void;
  isEditing: boolean;
  disabled?: boolean;
}) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex-1"
    >
      <Button type="button" onClick={onSubmit} disabled={disabled || submitting} className="w-full gap-2">
        <AnimatePresence mode="wait">
          {submitting ? (
            <motion.span
              key="submitting"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center gap-2"
            >
              <Loader2 className="h-4 w-4 animate-spin" />
              {isEditing ? "Saving..." : "Promoting..."}
            </motion.span>
          ) : (
            <motion.span
              key="idle"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center gap-2"
            >
              {isEditing ? <Save className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
              {isEditing ? "Save Changes" : "Promote"}
            </motion.span>
          )}
        </AnimatePresence>
      </Button>
    </motion.div>
  );
};

const PanelHeader = ({
  onClose,
  isEditing,
  statusChip,
}: {
  onClose: () => void;
  isEditing?: boolean;
  statusChip?: ReactNode;
}) => (
  <div className="flex shrink-0 items-center gap-1.5 pb-3">
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      className="size-9 shrink-0 rounded-full"
      onClick={onClose}
      aria-label="Back to details"
    >
      <ArrowLeft className="h-4 w-4" />
    </Button>
    <span className="min-w-0 flex-1 truncate text-xl font-normal tracking-tight text-foreground">
      {isEditing ? "Edit Promotion" : "Promote"}
    </span>
    {statusChip}
  </div>
);

export const PromotePanel = () => {
  const { file } = useFileDetailModal();
  return (
    <PromotePanelProvider key={file?.id ?? "no-file"}>
      <PromotePanelInner />
    </PromotePanelProvider>
  );
};
